import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Leaf, Eye, EyeSlash, Users, ShieldCheck } from '@phosphor-icons/react';

export default function InviteRegisterPage() {
    const { token } = useParams();
    const [invite, setInvite] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [name, setName] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const { api } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchInvite = async () => {
            try {
                const res = await api().get(`/auth/invite/${token}`);
                setInvite(res.data);
                setName(res.data.invitee_name || '');
            } catch (err) {
                setError(err.response?.data?.detail || 'Invalid or expired invitation');
            } finally {
                setLoading(false);
            }
        };
        fetchInvite();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!name) { toast.error('Please enter your name'); return; }
        if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
        if (password !== confirmPassword) { toast.error('Passwords do not match'); return; }

        setSubmitting(true);
        try {
            const res = await api().post('/auth/register-invited', { token, name, password });
            localStorage.setItem('token', res.data.access_token);
            toast.success('Welcome to Bush Rangers!');
            window.location.href = '/dashboard';
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Registration failed');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[hsl(53,92%,96%)] flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-[#22c55e] border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-[hsl(53,92%,96%)] dot-grid flex items-center justify-center p-4">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
                    <div className="brutalist-card p-6 md:p-8 text-center">
                        <div className="w-16 h-16 mx-auto rounded-full bg-[#ef4444] border-4 border-[#1e293b] flex items-center justify-center mb-4">
                            <ShieldCheck weight="fill" className="text-white" size={32} />
                        </div>
                        <h1 className="font-fredoka text-2xl font-bold text-[#1e293b] mb-2">Invitation Invalid</h1>
                        <p className="text-[#475569] mb-6">{error}</p>
                        <Link to="/" className="brutalist-btn bg-[#22c55e] text-white inline-block" data-testid="go-home-btn">
                            Go to Home
                        </Link>
                    </div>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[hsl(53,92%,96%)] dot-grid flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
                <div className="brutalist-card p-6 md:p-8">
                    <div className="text-center mb-8">
                        <div className="w-16 h-16 mx-auto rounded-full bg-[#22c55e] border-4 border-[#1e293b] flex items-center justify-center mb-4">
                            <Users weight="fill" className="text-white" size={32} />
                        </div>
                        <h1 className="font-fredoka text-2xl md:text-3xl font-bold text-[#1e293b]">
                            Join Bush Rangers
                        </h1>
                        <p className="text-[#475569] mt-2">
                            <strong>{invite.inviter_name}</strong> invited you to join as a parent
                        </p>
                        <p className="text-sm text-[#94a3b8] mt-1">
                            Signing up as {invite.email}
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div>
                            <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">Your Name</label>
                            <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                                className="brutalist-input w-full" placeholder="Your full name"
                                data-testid="invite-name-input" />
                        </div>
                        <div>
                            <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">Password</label>
                            <div className="relative">
                                <input type={showPassword ? 'text' : 'password'} value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="brutalist-input w-full pr-12" placeholder="At least 6 characters"
                                    data-testid="invite-password-input" />
                                <button type="button" onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[#475569]">
                                    {showPassword ? <EyeSlash size={24} /> : <Eye size={24} />}
                                </button>
                            </div>
                        </div>
                        <div>
                            <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">Confirm Password</label>
                            <input type="password" value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="brutalist-input w-full" placeholder="Re-enter password"
                                data-testid="invite-confirm-password" />
                        </div>
                        <motion.button type="submit" disabled={submitting} whileHover={{ y: -2, x: -2 }} whileTap={{ y: 2, x: 2 }}
                            className="brutalist-btn w-full bg-[#22c55e] text-white text-lg disabled:opacity-50" data-testid="invite-register-btn">
                            {submitting ? 'Creating Account...' : 'Create Account'}
                        </motion.button>
                    </form>

                    <div className="mt-6 text-center">
                        <p className="text-[#475569]">
                            Already have an account?{' '}
                            <Link to="/login" className="text-[#22c55e] font-semibold hover:underline">Log In</Link>
                        </p>
                    </div>
                </div>
            </motion.div>
        </div>
    );
}
